import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Plus, ArrowLeft, X, Info, Package, Sparkles } from 'lucide-react';
import { AIAnalysisService } from '../services/aiAnalysisService';
import { AIAnalysisRequest, AnalysisStatus, AnalysisError } from '../types/aiAnalysis';
import { processImageFiles, needsFormatConversion, getFormatName, validateImageFile } from '../utils/imageFormatConverter';

const AIAnalysisPage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // State management
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const [status, setStatus] = useState<AnalysisStatus>('idle');
  const [error, setError] = useState<AnalysisError | null>(null);
  const [additionalContext, setAdditionalContext] = useState('');
  const [showInfo, setShowInfo] = useState(false);
  
  const aiService = new AIAnalysisService();

  // Handle photo selection from camera or file input with format conversion
  const handlePhotoSelection = async (files: FileList | null) => {
    if (!files) return;

    setStatus('uploading');
    setError(null);

    try {
      const newPhotos = Array.from(files);
      
      // Validate files
      const validPhotos: File[] = [];
      const invalidPhotos: string[] = [];
      
      for (const file of newPhotos) {
        const validation = validateImageFile(file);
        if (validation.valid) {
          validPhotos.push(file);
        } else {
          invalidPhotos.push(`${file.name}: ${validation.error}`);
        }
      }
      
      if (invalidPhotos.length > 0) {
        setError({
          type: 'validation',
          message: `Vissa filer ignorerades: ${invalidPhotos.join(', ')}`,
          code: 'INVALID_FILES'
        });
      }
      
      if (validPhotos.length === 0) {
        setStatus('idle');
        return;
      }
      
      // Process files (convert formats if needed)
      const processedPhotos = await processImageFiles(validPhotos, (current, total, fileName) => {
        if (needsFormatConversion(validPhotos.find(f => f.name === fileName)!)) {
          console.log(`🔄 Konverterar ${getFormatName(validPhotos.find(f => f.name === fileName)!.type)}: ${fileName}`);
        }
      });

      const totalPhotos = [...selectedPhotos, ...processedPhotos];
      if (totalPhotos.length > 5) {
        setSelectedPhotos(totalPhotos.slice(0, 5));
        setError({
          type: 'validation',
          message: 'Maximum 5 foton per analys. Första 5 fotona valda.',
          code: 'TOO_MANY_PHOTOS'
        });
      } else {
        setSelectedPhotos(totalPhotos);
        setError(null);
      }
      
    } catch (err) {
      console.error('Photo processing error:', err);
      setError({
        type: 'validation',
        message: err instanceof Error ? err.message : 'Kunde inte bearbeta bilderna',
        code: 'PROCESSING_ERROR'
      });
    } finally {
      setStatus('idle');
    }
  };

  // Remove a selected photo
  const removePhoto = (index: number) => {
    const photoToRemove = selectedPhotos[index];
    setSelectedPhotos(prev => prev.filter((_, i) => i !== index));
    
    // Clean up the object URL for the removed photo
    if (photoUrls.has(photoToRemove)) {
      const url = photoUrls.get(photoToRemove);
      if (url) {
        URL.revokeObjectURL(url);
        setPhotoUrls(prev => {
          const newMap = new Map(prev);
          newMap.delete(photoToRemove);
          return newMap;
        });
      }
    }
    
    setError(null);
  };

  // Trigger file input
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // "Ta foto" goes through the file picker; on phones the user can pick the
  // native camera from there. (Previously this also requested a getUserMedia
  // stream it never used or stopped, which only triggered a permission prompt.)
  const startCamera = () => {
    triggerFileInput();
  };

  // Start AI analysis
  const startAnalysis = async () => {
    if (selectedPhotos.length === 0) {
      setError({
        type: 'validation',
        message: 'Välj minst ett foto för analys',
        code: 'NO_PHOTOS'
      });
      return;
    }

    if (!aiService.isConfigured()) {
      setError({
        type: 'api',
        message: 'AI-tjänsten är inte konfigurerad',
        code: 'NOT_CONFIGURED'
      });
      return;
    }

    try {
      setStatus('analyzing');
      setError(null);

      const request: AIAnalysisRequest = {
        photos: selectedPhotos,
        additionalContext: additionalContext.trim() || undefined
      };

      const result = await aiService.analyzeImages(request);
      
      // Store temporary result in sessionStorage and navigate to results
      sessionStorage.setItem('tempAnalysisResult', JSON.stringify(result));
      navigate(`/ai-analysis/results/${result.id}`);
      
    } catch (err) {
      console.error('Analysis failed:', err);
      setError(err as AnalysisError);
      setStatus('failed');
    }
  };

  // Store created object URLs for cleanup
  const [photoUrls, setPhotoUrls] = useState<Map<File, string>>(new Map());

  // Get photo preview URL with caching
  const getPhotoPreviewUrl = (file: File): string => {
    if (!photoUrls.has(file)) {
      const url = URL.createObjectURL(file);
      setPhotoUrls(prev => new Map(prev).set(file, url));
    }
    return photoUrls.get(file) || '';
  };

  // Clear all photos with proper cleanup
  const clearAllPhotos = () => {
    // Revoke all object URLs
    photoUrls.forEach(url => {
      URL.revokeObjectURL(url);
    });
    setPhotoUrls(new Map());
    setSelectedPhotos([]);
    setError(null);
  };

  // Get estimated cost
  const estimatedCost = aiService.getEstimatedCost(selectedPhotos.length);

  return (
    <div>
      {/* Header */}
      <div className="bg-[var(--bg-surface)] border-b border-[var(--border-default)]/80 sticky top-header z-20">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-accent-600 hover:text-accent-700"
          >
            <ArrowLeft size={20} className="mr-2" />
            <span className="text-base font-medium">Tillbaka</span>
          </button>
          
          <h1 className="text-lg font-semibold text-[var(--text-primary)]">
            AI Föremålsanalys
          </h1>
          
          <button
            onClick={() => setShowInfo(!showInfo)}
            aria-label="Visa information om AI-analys"
            aria-expanded={showInfo}
            className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-secondary)] rounded-full"
          >
            <Info size={20} />
          </button>
        </div>
      </div>

      {/* Info Panel */}
      {showInfo && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800/40 px-3 py-2">
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-medium mb-2">Så fungerar AI-analysen:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-400">
              <li>Ta 1-5 foton av föremålet från olika vinklar</li>
              <li>AI:n identifierar märke, modell, ålder och värde</li>
              <li>Kostnad: ~{estimatedCost.sek} SEK per analys</li>
              <li>Resultatet kan läggas till som nytt föremål</li>
            </ul>
          </div>
        </div>
      )}

      <div className="p-3 space-y-4">
        {/* Photo Capture Section */}
        <div className="bg-[var(--bg-surface)] rounded-xl border border-[var(--border-default)]/80 p-4">
          <div className="text-center">
            <div className="mb-3">
              <Package size={32} className="text-accent-500 mx-auto mb-2" />
              <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-2">
                Fotografera föremålet
              </h2>
              <p className="text-[var(--text-secondary)] text-sm">
                {selectedPhotos.length === 0 
                  ? 'Ta foton av föremålet från olika vinklar för bästa resultat'
                  : `${selectedPhotos.length}/5 foton valda`
                }
              </p>
            </div>

            {/* Photo Counter */}
            {selectedPhotos.length > 0 && (
              <div className="mb-4">
                <div className="flex justify-center items-center space-x-2">
                  {[1, 2, 3, 4, 5].map(num => (
                    <div
                      key={num}
                      className={`w-3 h-3 rounded-full ${
                        num <= selectedPhotos.length 
                          ? 'bg-accent-500' 
                          : 'bg-[var(--bg-surface-alt)]'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Photo Thumbnails */}
            {selectedPhotos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-4">
                {selectedPhotos.map((photo, index) => (
                  <div key={index} className="relative aspect-square">
                    <img
                      src={getPhotoPreviewUrl(photo)}
                      alt={`Foto ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg border-2 border-[var(--border-default)]"
                    />
                    <button
                      onClick={() => removePhoto(index)}
                      aria-label={`Ta bort foto ${index + 1}`}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-sm"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Capture Buttons */}
            <div className="space-y-3">
              <button
                onClick={startCamera}
                disabled={selectedPhotos.length >= 5 || status === 'analyzing'}
                className="w-full bg-accent-500 hover:bg-accent-600 disabled:bg-on-surface-tertiary disabled:cursor-not-allowed text-white font-medium py-4 px-6 rounded-xl flex items-center justify-center space-x-2 transition-colors"
              >
                <Camera size={20} />
                <span>Ta foto</span>
              </button>
              
              <button
                onClick={triggerFileInput}
                disabled={selectedPhotos.length >= 5 || status === 'analyzing'}
                className="w-full bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface-alt)] disabled:bg-[var(--bg-primary)] disabled:cursor-not-allowed text-[var(--text-secondary)] font-medium py-3 px-6 rounded-xl flex items-center justify-center space-x-2 transition-colors"
              >
                <Plus size={18} />
                <span>Välj från bibliotek</span>
              </button>
            </div>

            {/* Clear Photos Button */}
            {selectedPhotos.length > 0 && (
              <button
                onClick={clearAllPhotos}
                disabled={status === 'analyzing'}
                className="mt-3 text-sm text-red-600 hover:text-red-700 disabled:text-[var(--text-tertiary)]"
              >
                Rensa alla foton
              </button>
            )}
          </div>
        </div>

        {/* Additional Context */}
        {selectedPhotos.length > 0 && (
          <div className="bg-[var(--bg-surface)] rounded-xl border border-[var(--border-default)]/80 p-6">
            <h3 className="text-lg font-medium text-[var(--text-primary)] mb-3">
              Ytterligare information (valfritt)
            </h3>
            <textarea
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              placeholder="Beskriv föremålet mer detaljerat om du vill, t.ex. storlek, särskilda kännetecken, eller var du hittade det..."
              disabled={status === 'analyzing'}
              className="w-full p-2 border border-[var(--border-default)] rounded resize-none focus:ring-2 focus:ring-accent-500 focus:border-transparent disabled:bg-[var(--bg-primary)] disabled:text-[var(--text-secondary)]"
              rows={3}
              maxLength={500}
            />
            <div className="text-right text-xs text-[var(--text-secondary)] mt-1">
              {additionalContext.length}/500
            </div>
          </div>
        )}

        {/* Analysis Button */}
        {selectedPhotos.length > 0 && (
          <div className="bg-[var(--bg-surface)] rounded-xl border border-[var(--border-default)]/80 p-6">
            <div className="text-center">
              <div className="mb-4">
                <p className="text-sm text-[var(--text-secondary)] mb-2">
                  {estimatedCost.description}
                </p>
                <p className="text-xs text-[var(--text-secondary)]">
                  Kostnad: ~{estimatedCost.sek} SEK
                </p>
              </div>
              
              <button
                onClick={startAnalysis}
                disabled={status === 'analyzing' || selectedPhotos.length === 0}
                className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-on-surface-tertiary disabled:cursor-not-allowed text-white font-semibold py-4 px-6 rounded-xl flex items-center justify-center space-x-2 transition-colors"
              >
                {status === 'analyzing' ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Analyserar...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={20} />
                    <span>Starta AI-analys</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded-xl p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <X size={20} className="text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800 dark:text-red-300">
                  {error.message}
                </p>
                {error.code && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    Felkod: {error.code}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
                        accept="image/*,.heic,.heif,.avif"
        multiple
        onChange={(e) => handlePhotoSelection(e.target.files)}
        className="hidden"
      />
    </div>
  );
};

export default AIAnalysisPage; 